/**
 * Trimmed down version of Dashboard's React Select.
 * Internal logic slightly differs because:
 * 1. The use case is generally more constrained, so less edge case handling
 * 2. We may need to handle multiple thousands of options, so optimization is needed
 *
 * @see front-end/dashboard/components/ReactSelect/ReactSelect.tsx
 */
import { Spacing } from '@persona-id/design-tokens';
import Color from 'color';
import Fuse from 'fuse.js';
import { ComponentProps, useCallback, useContext, useMemo } from 'react';
import { components, StylesConfig } from 'react-select';
import Select from 'react-select/async';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import { BORDER_RADIUSES, COLORS, FONT_WEIGHTS, HEIGHTS } from 'shared/styles/variables';
import styled, { DefaultTheme, ThemeContext } from 'styled-components';

import { Elevation } from 'design-system/variables/effect';
import { BASE } from 'design-system/variables/spacing';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconCaretDown from 'images/icons/caret-down.svg';
import FormError from 'inquiry/components/FormError';
import { containerStyles, labelStyles } from 'inquiry/mixins/form';
import { InputMultiSelectComponent } from 'lib/interfaces/ui-components';
import { Z_INDICES } from 'verify/styles/variables';

export interface OptionType<ValueType = string> {
  disabled?: boolean;
  label: string;
  value?: ValueType;
}

type SelectProps = ComponentProps<typeof Select>;

export type ThemedFormMultiSelectProps = Omit<SelectProps, 'value' | 'onChange'> & {
  label: string;
  options: OptionType[];
  onChange?: (value: any) => void;
  onFocusOption?: (value: string) => void;
  components?: typeof components;
  disabled?: boolean;
  componentStyles?: InputMultiSelectComponent['styles'];
};

const Container = styled.div`
  ${containerStyles}
  ${labelStyles}
`;

const StyledIconCaretDown = styled(IconCaretDown)<{
  disabled: boolean;
}>`
  margin-right: ${Spacing[4]};

  display: inline-block;
  pointer-events: none;

  rect[fill],
  path[fill] {
    fill: ${(props) => props.theme.colorFont ?? COLORS.gray50};
  }
`;

// We pass {} to customStyles because we don't want original styles over-
// writing our component styles.
const getCustomStyles = (
  customStyles: StylesConfig,
  hasError: boolean,
  theme: DefaultTheme,
): StylesConfig => ({
  ...customStyles,
  container: (styles, state) => ({
    ...styles,
    width: '100%',

    ...(customStyles.container != null && customStyles.container({}, state)),
  }),
  control: (styles, state) => ({
    ...styles,
    // Cap at 4px
    // @see front-end/lib/styles/form.ts cappedBorderRadius
    backgroundColor: theme.colorInputBackground,
    borderRadius: Math.min(
      parseInt(theme.borderRadius ?? BORDER_RADIUSES.base, 10),
      parseInt(BORDER_RADIUSES.rounded),
    ),
    borderColor: state.menuIsOpen || state.isFocused ? theme.colorPrimary : theme.colorInputBorder,
    width: '100%',
    minHeight: HEIGHTS.input,
    boxShadow: state.isFocused
      ? `0 0 0 1px ${theme.colorPrimary}`
      : `0 0 0 0 ${theme.colorPrimary}`,
    '&:hover': {
      // no hover styles
    },
    transition: TransitionDuration.Fast,
    ...(hasError
      ? {
          borderColor: COLORS.red100,
          color: COLORS.red100,
          boxShadow: state.isFocused ? `0 0 0 1px ${COLORS.red100}` : `0 0 0 0 ${COLORS.red100}`,
        }
      : undefined),
    ...(customStyles.control != null && customStyles.control({}, state)),
  }),
  clearIndicator: (styles, state) => ({
    ...styles,
    paddingRight: 11, // Empirically derived
    ...(customStyles.clearIndicator != null && customStyles.clearIndicator({}, state)),
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    display: 'none',
    ...(customStyles.indicatorSeparator != null && customStyles.indicatorSeparator({}, state)),
  }),
  menu: (styles, state) => ({
    ...styles,
    backgroundColor: theme.colorDropdownBackground,
    boxShadow: Elevation.High,
    zIndex: Z_INDICES.dropdown,
    ...(customStyles.menu != null && customStyles.menu({}, state)),
  }),
  menuList: (styles, state) => ({
    ...styles,
    padding: BASE,
    ...(customStyles.menuList != null && customStyles.menuList({}, state)),
  }),
  multiValue: (styles, state) => ({
    ...styles,
    backgroundColor: theme.colorButtonPrimary ?? theme.colorPrimary,
    color: theme.colorFontButtonPrimary,
    fontFamily: theme.fontFamily,
    paddingLeft: BASE / 2,
    paddingRight: state.isDisabled ? BASE * 1.5 : 0,
    ...(customStyles.multiValue != null && customStyles.multiValue({}, state)),
  }),
  multiValueLabel: (styles, state) => ({
    ...styles,
    color: theme.colorFontButtonPrimary,
    fontWeight: parseInt(theme.buttonFontWeight ?? FONT_WEIGHTS.medium),
    ...(customStyles.multiValueLabel != null && customStyles.multiValueLabel({}, state)),
  }),
  multiValueRemove: (styles, state) => ({
    ...styles,
    color: theme.colorFontButtonPrimary,
    transitionDuration: TransitionDuration.Fast,
    ':hover': {
      // @see front-end/components/Button/Button.tsx colorButtonPrimaryLightened
      backgroundColor: Color(theme.colorButtonPrimary ?? theme.colorPrimary)
        .mix(Color('white'), 0.2)
        .hex(),
    },
    ':active': {
      // @see front-end/components/Button/Button.tsx colorButtonPrimaryLightened2
      backgroundColor: Color(theme.colorButtonPrimary ?? theme.colorPrimary)
        .mix(Color('white'), 0.4)
        .hex(),
    },
    ...(customStyles.multiValueRemove != null && customStyles.multiValueRemove({}, state)),
  }),
  option: (styles, state) => {
    const baseStyles = {
      ...styles,
      backgroundColor: 'transparent',
      borderRadius: BORDER_RADIUSES.rounded,
      color: theme.colorFont ?? COLORS.gray20,
      display: 'flex',
      fontFamily: theme.fontFamily,
      alignItems: 'center',
      padding: `${Spacing[3]} ${Spacing[2]}`,
      transitionDuration: TransitionDuration.Fast,
      ':active': {
        backgroundColor: theme.colorDropdownOption,
      },
    };
    const focusedStyles = state.isFocused
      ? {
          backgroundColor: theme.colorDropdownOption,
          ':active': {
            backgroundColor: theme.colorDropdownOption,
          },
        }
      : {};
    const disabledStyles = state.isDisabled
      ? {
          backgroundColor: 'transparent',
          color: COLORS.gray50,
          ':active': {
            backgroundColor: 'transparent',
          },
          overflow: 'auto',
        }
      : {};

    return {
      ...baseStyles,
      ...focusedStyles,
      ...disabledStyles,
      ...(customStyles.option != null && customStyles.option({}, state)),
    };
  },
  placeholder: (styles, state) => ({
    ...styles,
    color: theme.colorFont ?? COLORS.gray20,
    marginLeft: 0,
    marginRight: 0,
    opacity: 0.6,
    ...(hasError
      ? {
          color: COLORS.red100,
        }
      : undefined),
    ...(customStyles.placeholder != null && customStyles.placeholder({}, state)),
  }),
  valueContainer: (styles, state) => ({
    ...styles,
    fontSize: parseInt(theme.text.paragraph.md.fontSize, 10),
    fontFamily: theme.fontFamily,
    textDecoration: 'none',
    outline: '0 !important',
    padding: `2px ${Spacing[4]}`,
    ...(customStyles.valueContainer != null && customStyles.valueContainer({}, state)),
  }),
});

const useCustomComponents = (propComponents: Partial<typeof components>) =>
  useMemo(
    () => ({
      DropdownIndicator: (props: ComponentProps<typeof components.DropdownIndicator>) => {
        const { value, isDisabled } = props.selectProps;
        const theme = useContext(ThemeContext);

        // We only hide the dropdown indicator for multiselect. Always show for single.
        const hidden = Array.isArray(value) && value.length > 0;

        // overwrite React-Select props.theme
        return hidden ? null : <StyledIconCaretDown theme={theme} disabled={isDisabled} />;
      },
      MultiValueRemove: (props: ComponentProps<typeof components.MultiValueRemove>) => {
        const { isDisabled } = props.selectProps;

        return isDisabled ? null : <components.MultiValueRemove {...props} />;
      },
      ...propComponents,
    }),
    [],
  );

const OPTIONS_LENGTH_FOR_SYNC_SEARCH = 1000;

const ThemedFormMultiSelect = ({
  className,
  components: propComponents,
  id,
  input: { value, onBlur, onChange, ...inputProps },
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  isOptionDisabled = (opt: OptionType) => !!opt.disabled,
  label,
  meta: { error, submitFailed },
  onFocusOption,
  options,
  placeholder,
  styles: customStyles = {},
  componentStyles,
  ...passthroughProps
}: ThemedFormMultiSelectProps & WrappedFieldProps) => {
  const theme = useContext(ThemeContext);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const components = useCustomComponents(propComponents);

  /**
   * Used to preserve order of changed values without relying on many O(N) calls
   */
  const optionsIndexMap = useMemo(
    () =>
      options.reduce<Record<string, number>>((map, option: OptionType, index) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        map[option.value] = index;
        return map;
      }, {}),
    [options],
  );

  /**
   * Handles conversion between Redux form and React select value formats:
   *
   * Singular value:
   *   Redux form: 'grape'
   *   React select: { value: 'grape', label: 'Grape' }
   *
   * Multi value:
   *   Redux form: ['grape']
   *   React select: [{ value: 'grape', label: 'Grape' }]
   *
   * Ensures incoming values are always in fixed order, matching options order.
   */
  const transformedValue = useMemo(() => {
    if (value == null || value === '') {
      return [];
    }

    // Equivalent to options.filter(o => value.some(v => o === v)) but not linear time
    return (value as string[]).map((v) => optionsIndexMap[v]).map((index) => options[index]);
  }, [value, optionsIndexMap]);

  const fuse = useMemo(() => {
    // Only start searching when 2 characters are inputted.
    // Require exact matches, but allow matches to be anywhere in the string.
    const fuseOptions = {
      minMatchCharLength: 1,
      threshold: 0.4,
      keys: [
        { name: 'label', weight: 0.9 },
        { name: 'value', weight: 0.1 },
      ],
    };
    return new Fuse(options, fuseOptions);
  }, [options]);

  const useAsyncSearch = options.length > OPTIONS_LENGTH_FOR_SYNC_SEARCH;

  const loadOptions = useCallback(
    (inputValue: string) =>
      new Promise<OptionType[]>((resolve) => {
        setTimeout(
          () => resolve(fuse.search(inputValue).map(({ item }) => item)),
          useAsyncSearch ? 250 : 0,
        );
      }),
    [fuse],
  );

  const defaultOptions = useMemo(
    () => (useAsyncSearch ? options.slice(0, OPTIONS_LENGTH_FOR_SYNC_SEARCH) : options),
    [options],
  );

  const handleBlur = useCallback(
    () =>
      // [PER-9753] Call Redux-form onBlur with `undefined` to fix mobile devices not being able to select values.
      // `blurInputOnSelect` defaults to `true` on mobile devices, which causes the blur event to fire once an option
      // is clicked. Because the change action has not resolved yet, `input.value` is still `''`, so the blur event
      // will cause the selection to be a no-op.
      // https://github.com/JedWatson/react-select/issues/2692#issuecomment-488541149
      onBlur(undefined),
    [onBlur, value],
  );

  /**
   * Ensures outputted values are always in fixed order, matching options order.
   */
  const handleChange = useCallback(
    (selected: OptionType[]) => {
      const stablySortedValue = (selected ?? [])
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        .map((option) => optionsIndexMap[option.value])
        .sort((a, b) => a - b)
        .map((index) => options[index].value);
      onChange(stablySortedValue);
    },
    [onChange, optionsIndexMap, options],
  );

  const hasError = !!(error && submitFailed);
  const styles = getCustomStyles(customStyles, hasError, theme);

  return (
    <Container>
      {label && (
        <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: id }}>
          {label}
        </Markdown>
      )}
      <Select
        {...inputProps}
        id={id}
        isMulti
        isSearchable
        menuPlacement='auto'
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        {...passthroughProps}
        // support both Persona convention and original prop
        isDisabled={passthroughProps.disabled || passthroughProps.isDisabled}
        isOptionDisabled={isOptionDisabled}
        onBlur={handleBlur}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onChange={handleChange}
        value={transformedValue}
        options={options}
        placeholder={placeholder}
        className={className}
        styles={styles}
        components={components}
        onFocusOption={onFocusOption}
        aria-invalid={hasError}
        aria-errormessage={`${id}--error`}
        aria-describedby={`${id}--error`}
      />
      <FormError
        id={`${id}--error`}
        hasError={hasError}
        errorMessage={error}
        styles={componentStyles}
      />
    </Container>
  );
};

export default ThemedFormMultiSelect;
